// Real motion from licensed sources, imported by apps/web/scripts/motion (CMU motion capture, MM-Fit and Wikimedia
// Commons videos run through the recording lane), and the exercises each clip shows. Clip files are in
// public/demos/clips/<id>.json; credits and license texts in public/demos/LICENSES.txt. An exercise's own recorded
// demo comes first; an imported clip comes before the built-in clip the catalog names (registry.ts demoFor).
//
// CMU clips ship only inside the app (CMU's terms: no resale of the data, even converted), never as a downloadable
// collection. Clips derived from CC BY-SA videos are published under CC BY-SA 4.0 (only those clip files).

export type ImportSource = 'cmu' | 'mmfit' | 'commons';

export interface ImportedDemo {
  /** Clip id, the file name in public/demos/clips/. */
  id: string;
  source: ImportSource;
  /** License of the motion clip. */
  license: string;
  /** The credit line on the exercise page: the text, the source it links to and the license text it links to. */
  credit: { text: string; url: string; licenseUrl: string };
  /** Catalog exercises shown with this motion: the exact move and close variants. */
  exercises: string[];
  /** Hinge or squat depth matters: the session adds a small side view. */
  sideView: boolean;
  kettlebell: boolean;
}

/** CMU asks for this acknowledgment (mocap.cs.cmu.edu). */
export const CMU_ACK = 'The data used in this project was obtained from mocap.cs.cmu.edu. The database was created with funding from NSF EIA-0196217.';
const CMU_URL = 'http://mocap.cs.cmu.edu/';
const cmu = (trial: string) => ({ text: `Motion capture from the CMU Graphics Lab Motion Capture Database (mocap.cs.cmu.edu, take ${trial}), funded by NSF EIA-0196217`, url: CMU_URL, licenseUrl: CMU_URL });
const CMU_LICENSE = 'CMU Graphics Lab Motion Capture Database terms (use in products allowed, no resale)';

/** The license texts the credit line links to. */
export const LICENSE_URLS: Record<string, string> = {
  'CC BY 4.0': 'https://creativecommons.org/licenses/by/4.0/',
  'CC BY 3.0': 'https://creativecommons.org/licenses/by/3.0/',
  'CC BY-SA 4.0': 'https://creativecommons.org/licenses/by-sa/4.0/',
};

const MMFIT_URL = 'https://zenodo.org/records/7672767';
const mmfit = (session: string) => ({ text: `Motion read from an MM-Fit video (session ${session}; Strömbäck, Huang and Radu, IMWUT 2020), CC BY 4.0`, url: MMFIT_URL, licenseUrl: LICENSE_URLS['CC BY 4.0']! });

const commons = (title: string, author: string, license: string) => ({
  text: `Motion read from "${title}" by ${author}, Wikimedia Commons, ${license}${license.startsWith('CC BY-SA') ? '; this motion clip is shared under CC BY-SA 4.0' : ''}`,
  url: `https://commons.wikimedia.org/wiki/File:${title.replace(/ /g, '_')}`,
  licenseUrl: LICENSE_URLS[license] ?? LICENSE_URLS['CC BY 4.0']!,
});
const FS = 'FitnessScape';

export const IMPORTED: ImportedDemo[] = [
  // CMU motion capture
  { id: 'cmu-squat', source: 'cmu', license: CMU_LICENSE, credit: cmu('86_02'), exercises: ['squat', 'pause_squat'], sideView: true, kettlebell: false },
  { id: 'cmu-torso-twist', source: 'cmu', license: CMU_LICENSE, credit: cmu('13_29'), exercises: ['torso_twists'], sideView: false, kettlebell: false },
  { id: 'cmu-high-knee-march', source: 'cmu', license: CMU_LICENSE, credit: cmu('13_29'), exercises: ['marching_high_knees'], sideView: false, kettlebell: false },
  // MM-Fit videos (CC BY 4.0)
  { id: 'mmfit-shoulder-press', source: 'mmfit', license: 'CC BY 4.0', credit: mmfit('w19'), exercises: ['dumbbell_shoulder_press'], sideView: false, kettlebell: false },
  // Wikimedia Commons videos
  { id: 'commons-romanian-deadlift', source: 'commons', license: 'CC BY 3.0', credit: commons('Deadlift - exercise demonstration video.webm', FS, 'CC BY 3.0'), exercises: ['romanian_deadlift'], sideView: true, kettlebell: false },
  { id: 'commons-romanian-deadlift-kb', source: 'commons', license: 'CC BY 3.0', credit: commons('Deadlift - exercise demonstration video.webm', FS, 'CC BY 3.0'), exercises: ['kettlebell_rdl'], sideView: true, kettlebell: true },
];
